import { canPlaceAt, canPlaceDecorAt, waterCellAt, waterCellCenter, waterKey, type GardenPlacement } from "./garden";
import { decorDef, hostSlots, type DecorId, type DecorPlacement } from "./decor";
import type { TerrainType } from "./terrain";
import { FLOWERS } from "./flowers";
import { ACHIEVEMENTS } from "./achievements";
import { harvestAchievementId } from "./achievementFamilies";
import { LEGEND_THRESHOLD } from "./legendPlants";
import type { SaveData } from "./save";
import type { Plot } from "../render/plot";

/**
 * SHOWCASE SAVES — five fully dressed gardens and five fully stocked greenhouses, for marketing screenshots
 * (the Kickstarter page). `/play?shot=garden&showcase=3` loads variant 3; the same number drives BOTH scenes,
 * since the garden and greenhouse halves of a save never touch. Dev-only: reached through shotMode, which is
 * inert outside the Vite dev server. `scripts/showcase.mjs` captures the set through the game's own photo mode.
 *
 * Every garden item goes through the SAME legality checks the place flow runs (canPlaceDecorAt/canPlaceAt,
 * against the plot, the painted water and everything accepted before it), so a showcase can never picture a
 * layout a player couldn't build. An illegal entry is dropped with a console warning — if an item goes
 * missing from a shot, that warning says why. Order matters: water and paths first, then decor, then hosted
 * plants, then single plants, then the filler beds (which simply skip spots that are taken).
 *
 * Coordinates are garden units. A plot cell (cx,cy) spans [7cx−3.5, 7cx+3.5] on each axis; on screen +gx
 * runs down-RIGHT and +gy down-LEFT, so the lowest gx+gy is the BACK of the plot — trees and tall pieces live
 * there so they don't hide the beds in front of them.
 */

type Pt = [number, number];
interface GardenLayout {
  cells: Pt[];
  ponds?: Array<{ c: Pt; rx: number; ry: number }>;
  streams?: Array<{ pts: Pt[]; r: number }>;
  paths?: Array<{ type: TerrainType; pts: Pt[]; r: number }>;
  fences?: string[];
  decor: Array<[DecorId, number, number, boolean?]>;
  /** a planting host and the species for each of its slots */
  hosts?: Array<[DecorId, number, number, string[]]>;
  plants?: Array<[string, number, number]>;
  /** filler beds: species picked over a jittered grid inside a circle; `fill` < 1 thins it to a meadow scatter */
  beds?: Array<{ c: Pt; r: number; species: string[]; step?: number; fill?: number }>;
  /** planted rows: species picked along a straight line (the harvest plots) */
  rows?: Array<{ from: Pt; to: Pt; species: string[]; step?: number }>;
}

// deterministic jitter so a showcase renders identically every run
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
}

function distToPolyline(p: Pt, pts: Pt[]): number {
  let best = Infinity;
  for (let i = 0; i + 1 < pts.length; i++) {
    const [ax, ay] = pts[i]!, [bx, by] = pts[i + 1]!;
    const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - ay) * dy) / L2));
    best = Math.min(best, Math.hypot(p[0] - (ax + t * dx), p[1] - (ay + t * dy)));
  }
  return best;
}

/** Every water/terrain sub-cell whose centre passes `inside`, scanned over a bounding box. */
function cellsWhere(x0: number, y0: number, x1: number, y1: number, inside: (g: Pt) => boolean): string[] {
  const out: string[] = [];
  const a = waterCellAt(x0, y0), b = waterCellAt(x1, y1);
  for (let wx = a.wx; wx <= b.wx; wx++) for (let wy = a.wy; wy <= b.wy; wy++) {
    const c = waterCellCenter(wx, wy);
    if (inside([c.gx, c.gy])) out.push(waterKey(wx, wy));
  }
  return out;
}

function polyBox(pts: Pt[], pad: number): [number, number, number, number] {
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  return [Math.min(...xs) - pad, Math.min(...ys) - pad, Math.max(...xs) + pad, Math.max(...ys) + pad];
}

function buildGarden(L: GardenLayout, seed: number): Partial<SaveData> {
  const plot: Plot = { cells: L.cells.map(([cx, cy]) => ({ cx, cy })) };
  const water = new Set<string>();
  for (const p of L.ponds ?? []) {
    for (const k of cellsWhere(p.c[0] - p.rx - 1, p.c[1] - p.ry - 1, p.c[0] + p.rx + 1, p.c[1] + p.ry + 1,
      ([x, y]) => ((x - p.c[0]) / p.rx) ** 2 + ((y - p.c[1]) / p.ry) ** 2 <= 1)) water.add(k);
  }
  for (const s of L.streams ?? []) {
    const [x0, y0, x1, y1] = polyBox(s.pts, s.r + 1);
    for (const k of cellsWhere(x0, y0, x1, y1, (g) => distToPolyline(g, s.pts) <= s.r)) water.add(k);
  }
  const terrain: Record<string, TerrainType> = {};
  for (const path of L.paths ?? []) {
    const [x0, y0, x1, y1] = polyBox(path.pts, path.r + 1);
    for (const k of cellsWhere(x0, y0, x1, y1, (g) => distToPolyline(g, path.pts) <= path.r)) if (!water.has(k)) terrain[k] = path.type;
  }

  const decor: DecorPlacement[] = [];
  const garden: GardenPlacement[] = [];
  let n = 0;
  const addDecor = (id: DecorId, gx: number, gy: number, flip?: boolean): string | null => {
    if (!canPlaceDecorAt(plot, decorDef(id).footprint, garden, decor, gx, gy, water)) {
      console.warn(`[showcase] decor ${id} at ${gx},${gy} is not a legal placement — dropped`);
      return null;
    }
    const d: DecorPlacement = { id: `sc-d${n++}`, decor: id, gx, gy, seed: (seed * 31 + n * 17) % 997, ...(flip ? { flip: true } : {}) };
    decor.push(d);
    return d.id;
  };
  const known = (sp: string) => { if (FLOWERS[sp]) return true; console.warn(`[showcase] unknown species ${sp}`); return false; };

  for (const [id, gx, gy, flip] of L.decor) addDecor(id, gx, gy, flip);
  for (const [id, gx, gy, species] of L.hosts ?? []) {
    const hostId = addDecor(id, gx, gy);
    if (!hostId) continue;
    species.slice(0, hostSlots(id)).forEach((sp, slot) => {
      if (known(sp)) garden.push({ id: `sc-p${n++}`, species: sp, gx, gy, hostId, slot });
    });
  }
  const tryPlant = (sp: string, gx: number, gy: number, quiet = false) => {
    if (!known(sp)) return false;
    // plants stay off the painted paths as well as the water
    if (terrain[(() => { const c = waterCellAt(gx, gy); return waterKey(c.wx, c.wy); })()]) return false;
    if (!canPlaceAt(plot, garden, gx, gy, water, undefined, decor)) {
      if (!quiet) console.warn(`[showcase] plant ${sp} at ${gx},${gy} is not a legal placement — dropped`);
      return false;
    }
    garden.push({ id: `sc-p${n++}`, species: sp, gx, gy });
    return true;
  };
  for (const [sp, gx, gy] of L.plants ?? []) tryPlant(sp, gx, gy);
  const rand = rng(seed * 7919 + 1);
  for (const bed of L.beds ?? []) {
    const step = bed.step ?? 0.72;
    let i = 0;
    for (let y = bed.c[1] - bed.r; y <= bed.c[1] + bed.r; y += step) {
      for (let x = bed.c[0] - bed.r; x <= bed.c[0] + bed.r; x += step) {
        const jx = x + (rand() - 0.5) * step * 0.5, jy = y + (rand() - 0.5) * step * 0.5;
        if ((jx - bed.c[0]) ** 2 + (jy - bed.c[1]) ** 2 > bed.r * bed.r) continue;
        const sp = bed.species[Math.floor(rand() * bed.species.length)]!;
        if (rand() > (bed.fill ?? 1)) continue;
        if (tryPlant(sp, jx, jy, true)) i++;
      }
    }
    if (i === 0) console.warn(`[showcase] bed at ${bed.c} placed nothing`);
  }

  for (const row of L.rows ?? []) {
    const step = row.step ?? 0.7;
    const len = Math.hypot(row.to[0] - row.from[0], row.to[1] - row.from[1]);
    for (let d = 0; d <= len; d += step) {
      const t = d / (len || 1);
      const sp = row.species[Math.floor(rand() * row.species.length)]!;
      tryPlant(sp, row.from[0] + (row.to[0] - row.from[0]) * t + (rand() - 0.5) * 0.12, row.from[1] + (row.to[1] - row.from[1]) * t + (rand() - 0.5) * 0.12, true);
    }
  }

  const decorOwned: Record<string, number> = {};
  for (const d of decor) decorOwned[d.decor] = (decorOwned[d.decor] ?? 0) + 1;
  return { plot, gardenWater: [...water], gardenTerrain: terrain, fences: L.fences ?? [], decor, garden, decorOwned };
}

// local-space helper: author a layout in plot-local units (0,0 = the back corner) and shift it into garden units
const at = (x0: number, y0: number) => (u: number, v: number): Pt => [x0 + u, y0 + v];

/** Fence keys along a run of posts: "h" edges step +py at constant px, "v" edges step +px at constant py. */
function fenceRun(dir: "h" | "v", fixed: number, from: number, to: number): string[] {
  const out: string[] = [];
  for (let k = from; k < to; k++) out.push(dir === "h" ? `${fixed},${k},h` : `${k},${fixed},v`);
  return out;
}

function gardenLayout(v: number): GardenLayout {
  switch (v) {
    // 1 — COTTAGE STREAM: a brook cutting the plot diagonally, cottage beds either side, cherry in bloom.
    case 1: {
      const P = at(-10.5, -10.5);   // 2×2 plot, local 0..14
      const d = (id: DecorId, u: number, w: number, flip?: boolean): [DecorId, number, number, boolean?] => [id, ...P(u, w), flip];
      return {
        cells: [[-1, -1], [0, -1], [-1, 0], [0, 0]],
        streams: [{ pts: [P(14.8, 3.2), P(11.5, 4.6), P(8.6, 6.9), P(6.4, 10.2), P(5.2, 14.8)], r: 0.85 }],
        paths: [{ type: "pebble", pts: [P(13.6, 13.6), P(11.8, 11.4), P(10.9, 9.6)], r: 0.6 }],
        fences: [...fenceRun("h", -5, -5, 0), ...fenceRun("v", -5, -5, -1)],
        decor: [
          d("cherryTree", 2.4, 2.6), d("birchTree", 1.2, 8.6), d("pineTree", 7.2, 1.2),
          d("gardenBench", 12.4, 12.0), d("birdBath", 12.8, 8.2), d("lantern", 9.6, 12.8),
          d("sundial", 12.4, 1.4), d("wateringCan", 13.3, 10.3), d("pinwheel", 10.8, 13.3),
          d("birdhouse", 4.6, 5.4), d("rockCairn", 10.0, 2.6), d("mossyRock", 8.9, 4.2), d("windChime", 3.6, 12.4),
          d("stump", 1.6, 12.8), d("pebbles", 4.8, 11.0),
        ],
        hosts: [
          ["planterBox", ...P(13.4, 1.8), ["nasturtium"]],
          ["windowBox", ...P(8.2, 11.3), ["ivyGeranium", "trailingLobelia", "sweetPea"]],
          ["stoneUrn", ...P(5.6, 7.8), ["creepingJenny"]],
        ],
        beds: [
          { c: P(3.8, 9.0), r: 1.7, species: ["foxglove", "delphinium", "lavenderShrub", "peony", "hydrangeaBush", "rose"] },
          { c: P(4.6, 3.0), r: 1.3, species: ["foxglove", "delphinium", "allium", "bluebell"] },
          { c: P(10.8, 7.8), r: 1.0, species: ["daisy", "tulip", "poppy", "marigold"] },
          { c: P(10.6, 1.2), r: 1.0, species: ["cosmos", "zinnia", "pansy"] },
          { c: P(12.2, 3.2), r: 0.6, species: ["cosmos", "pansy"] },
          { c: P(9.2, 13.4), r: 0.8, species: ["daisy", "crocus", "anemone"] },
          { c: P(13.2, 7.0), r: 0.9, species: ["lavenderShrub", "daisy", "pansy"] },
          { c: P(8.2, 5.2), r: 0.8, species: ["papyrus", "horsetail", "fountainGrass"] },
          { c: P(6.9, 12.6), r: 0.6, species: ["sedge", "horsetail"] },
          { c: P(1.4, 5.4), r: 1.0, species: ["hydrangeaBush", "azalea", "rhododendron"] },
        ],
      };
    }
    // 2 — MOONLIT POND: a wide 3×2 plot around a still pond, lit by lanterns, torches, a fire pit and glow plants.
    case 2: {
      const P = at(-10.5, -10.5);   // 3×2 plot, local u 0..21, v 0..14
      const d = (id: DecorId, u: number, w: number, flip?: boolean): [DecorId, number, number, boolean?] => [id, ...P(u, w), flip];
      return {
        cells: [[-1, -1], [0, -1], [1, -1], [-1, 0], [0, 0], [1, 0]],
        ponds: [{ c: P(10.6, 7.4), rx: 3.4, ry: 2.6 }],
        paths: [{ type: "stone", pts: [P(19.5, 13.6), P(16.6, 11.6), P(15.2, 9.4)], r: 0.55 }],
        decor: [
          d("willowTree", 6.2, 3.4), d("cherryTree", 16.2, 2.6), d("pineTree", 2.2, 2.4), d("poplarTree", 11.6, 1.2),
          d("fairyLights", 5.2, 10.6), d("fairyLights", 13.6, 12.4, true),
          d("lantern", 7.2, 10.4), d("lantern", 14.8, 5.2), d("lantern", 9.6, 3.6),
          d("torch", 3.4, 12.2), d("torch", 19.4, 9.4), d("torch", 18.4, 4.6),
          d("firePit", 17.2, 11.8), d("gardenBench", 19.0, 12.6, true), d("log", 15.6, 12.9),
          d("gazingBall", 11.0, 12.3), d("obelisk", 1.6, 7.6), d("stump", 2.8, 9.6), d("mossyRock", 14.4, 8.8),
        ],
        hosts: [
          ["hangingBasket", ...P(13.0, 10.6), ["glassvine"]],
          ["stoneUrn", ...P(8.2, 12.6), ["cometvine"]],
          ["hangingBasket", ...P(4.0, 6.2), ["stringHearts"]],
        ],
        beds: [
          { c: P(3.6, 4.6), r: 1.6, species: ["nightglowMushroom", "lumenCactus", "lanternMoss", "pearlFern"] },
          { c: P(18.6, 7.0), r: 1.6, species: ["crystalbloom", "willowisp", "voidOrchid", "auroraFern"] },
          { c: P(10.8, 11.0), r: 1.1, species: ["waterDandelion", "lightbloom", "glacierlily"] },
          { c: P(6.0, 7.6), r: 1.0, species: ["willowisp", "frostfern", "nightglowMushroom"] },
          { c: P(14.8, 7.2), r: 0.9, species: ["lumenCactus", "lanternMoss", "abyssBlossom"] },
          { c: P(20.0, 2.4), r: 1.0, species: ["lavenderShrub", "hydrangeaBush", "pearlFern"] },
          { c: P(1.4, 12.6), r: 0.9, species: ["pearlFern", "nightglowMushroom"] },
        ],
      };
    }
    // 3 — RAINY WOODLAND: evergreens and birch over a spring-fed pool and brook, ferns, moss and a woodsman's corner.
    case 3: {
      const P = at(-10.5, -10.5);   // 2×2 plot
      const d = (id: DecorId, u: number, w: number, flip?: boolean): [DecorId, number, number, boolean?] => [id, ...P(u, w), flip];
      return {
        cells: [[-1, -1], [0, -1], [-1, 0], [0, 0]],
        ponds: [{ c: P(5.4, 5.8), rx: 2.2, ry: 1.7 }],
        streams: [{ pts: [P(6.8, 6.6), P(9.0, 8.6), P(11.2, 9.4), P(14.8, 11.2)], r: 0.75 }],
        paths: [{ type: "dirt", pts: [P(13.6, 1.4), P(12.0, 4.6), P(9.4, 12.0), P(8.6, 14.2)], r: 0.6 }],
        decor: [
          d("pineTree", 1.6, 1.8), d("birchTree", 8.6, 1.6),
          d("pineTree", 1.2, 10.4), d("birchTree", 3.2, 12.8),
          d("boulder", 2.6, 7.4), d("mossyRock", 7.6, 4.0), d("mossyRock", 11.4, 11.8), d("flatBoulder", 6.8, 9.6),
          d("log", 10.4, 6.6), d("stump", 13.2, 7.2), d("woodpile", 13.4, 3.8), d("logStore", 11.2, 2.0),
          d("rainBarrel", 13.8, 9.6), d("waterPump", 7.8, 12.6), d("rockCairn", 5.2, 10.6),
        ],
        hosts: [["terracottaPots", ...P(12.6, 13.2), ["englishIvy"]]],
        beds: [
          { c: P(3.8, 3.4), r: 1.2, species: ["bostonFern", "maidenhairFern", "birdsNestFern"] },
          { c: P(4.2, 9.2), r: 1.2, species: ["asparagusFern", "bostonFern", "foxglove", "bluebell"] },
          { c: P(8.0, 7.0), r: 0.9, species: ["horsetail", "papyrus", "sedge"] },
          { c: P(10.4, 10.8), r: 1.0, species: ["mondoGrass", "blueFescue", "anemone"] },
          { c: P(6.2, 12.8), r: 1.0, species: ["bluebell", "crocus", "maidenhairFern"] },
          { c: P(10.2, 4.4), r: 0.9, species: ["foxglove", "staghornFern", "buttonFern"] },
          { c: P(13.0, 12.0), r: 0.8, species: ["sedge", "blueFescue"] },
          { c: P(1.6, 5.4), r: 1.1, species: ["foxglove", "bluebell", "bostonFern"] },
          { c: P(5.4, 1.4), r: 1.0, species: ["maidenhairFern", "anemone", "birdsNestFern"] },
          { c: P(9.4, 12.9), r: 0.9, species: ["bluebell", "crocus", "anemone"] },
          { c: P(12.4, 5.6), r: 0.8, species: ["foxglove", "asparagusFern"] },
        ],
      };
    }
    // 4 — FORMAL FOUNTAIN COURT: a symmetrical parterre — paver cross, four clipped beds, topiary, urns, poplars.
    case 4: {
      const P = at(-10.5, -10.5);   // 2×2 plot, centre (7,7)
      const d = (id: DecorId, u: number, w: number, flip?: boolean): [DecorId, number, number, boolean?] => [id, ...P(u, w), flip];
      const quad = (cu: number, cv: number, sp: string[]) => ({ c: P(cu, cv), r: 1.55, species: sp, step: 0.7 });
      return {
        cells: [[-1, -1], [0, -1], [-1, 0], [0, 0]],
        paths: [
          { type: "stone", pts: [P(7, 0.4), P(7, 13.6)], r: 0.7 },
          { type: "stone", pts: [P(0.4, 7), P(13.6, 7)], r: 0.7 },
        ],
        fences: [...fenceRun("h", -5, -5, 1), ...fenceRun("v", -5, -5, 1)],
        decor: [
          d("fountain", 7, 7),
          d("poplarTree", 1.2, 1.2), d("poplarTree", 12.8, 1.2), d("poplarTree", 1.2, 12.8),
          d("topiarySpiral", 5.0, 5.0), d("topiarySpiral", 9.0, 5.0), d("topiarySpiral", 5.0, 9.0), d("topiarySpiral", 9.0, 9.0),
          d("topiaryBall", 7, 1.8), d("topiaryBall", 1.8, 7), d("topiaryBall", 12.2, 7), d("topiaryBall", 7, 12.2),
          d("obelisk", 3.2, 3.2), d("armillary", 11.2, 11.2), d("gardenBench", 12.8, 4.4), d("gardenBench", 4.4, 12.8, true),
          d("lantern", 8.8, 3.0), d("lantern", 3.0, 8.8), d("sundial", 10.6, 2.6),
        ],
        hosts: [
          ["stoneUrn", ...P(5.3, 12.4), ["englishIvy"]], ["stoneUrn", ...P(12.4, 5.3), ["ivyGeranium"]],
          ["stoneUrn", ...P(8.7, 1.4), ["trailingLobelia"]],
        ],
        beds: [
          quad(3.6, 3.6, ["rose", "miniRoseBush", "lavenderShrub"]),
          quad(10.4, 3.6, ["tulip", "hyacinth", "boxwood"]),
          quad(3.6, 10.4, ["tulip", "hyacinth", "boxwood"]),
          quad(10.4, 10.4, ["rose", "peony", "lavenderShrub"]),
          { c: P(12.9, 12.9), r: 0.9, species: ["hydrangeaBush", "gardenia"] },
        ],
      };
    }
    // 5 — AUTUMN HOMESTEAD: maples ablaze, a working corner (hay, barrow, potting bench), sunflower beds, a well.
    default: {
      const P = at(-10.5, -10.5);   // 3×2 plot, local u 0..21, v 0..14
      const d = (id: DecorId, u: number, w: number, flip?: boolean): [DecorId, number, number, boolean?] => [id, ...P(u, w), flip];
      return {
        cells: [[-1, -1], [0, -1], [1, -1], [-1, 0], [0, 0], [1, 0]],
        ponds: [{ c: P(17.4, 10.2), rx: 1.9, ry: 1.5 }],
        paths: [{ type: "dirt", pts: [P(20.6, 3.2), P(14.6, 5.6), P(9.4, 8.4), P(3.0, 13.6)], r: 0.7 }],
        fences: [...fenceRun("v", 0, -5, -2), ...fenceRun("h", -2, 0, 1)],
        decor: [
          d("mapleTree", 2.6, 2.4), d("mapleTree", 17.8, 1.6), d("oakTree", 9.8, 1.6),
          d("wishingWell", 13.0, 10.6), d("dovecote", 5.0, 6.6), d("dovecote", 3.2, 8.4, true),
          d("wheelbarrow", 12.0, 7.4), d("pottingBench", 7.4, 3.8), d("barrel", 5.8, 1.4), d("rainBarrel", 14.6, 2.6),
          d("gardenArch", 19.8, 6.4), d("coldFrame", 10.4, 5.6), d("hammock", 8.8, 5.8), d("wateringCan", 15.2, 7.8),
          d("woodpile", 1.0, 12.0), d("birdhouse", 20.2, 12.0), d("signpost", 16.6, 6.6),
        ],
        hosts: [
          ["windowBox", ...P(10.4, 12.6), ["nasturtium", "creepingJenny", "nasturtium"]],
          ["terracottaPots", ...P(15.4, 12.8), ["ivyGeranium"]],
        ],
        beds: [
          { c: P(3.6, 11.2), r: 1.4, species: ["sunflower", "blackEyedSusan", "marigold"] },
          { c: P(8.4, 11.6), r: 1.2, species: ["zinnia", "gerbera", "chrysanthemum", "marigold"] },
          { c: P(14.2, 4.4), r: 1.0, species: ["pepperPlant", "winterberry", "holly"] },
          { c: P(19.0, 9.0), r: 1.0, species: ["pampasGrass", "purpleMillet", "sedge"] },
          { c: P(12.0, 3.2), r: 1.1, species: ["sunflower", "gladiolus", "blackEyedSusan"] },
          { c: P(6.6, 9.4), r: 0.8, species: ["chrysanthemum", "aster", "marigold"] },
          { c: P(20.0, 1.0), r: 0.7, species: ["pampasGrass", "purpleMillet"] },
          { c: P(6.8, 13.1), r: 0.8, species: ["marigold", "zinnia", "gerbera"] },
          { c: P(13.0, 13.2), r: 0.7, species: ["chrysanthemum", "aster"] },
          { c: P(1.6, 6.0), r: 0.9, species: ["sunflower", "marigold"] },
          { c: P(20.2, 13.2), r: 0.6, species: ["pampasGrass", "sedge"] },
        ],
      };
    }
  }
}


// ── FULL-BLEED gardens (6..10): a 6×6 plot bigger than the frame, shot from a small window at high density, so
//    the ground fills the picture edge to edge and no sky shows. Only what falls inside the frame is dressed, so
//    these are authored in SCREEN space: b = left/right (−15.6..15.6 is the frame), a = up/down (−17.6..17.6).
//    One b unit is 46 css px, one a unit 23 css px; `F(b, a)` converts to garden units. Trees stand along the top
//    edge on purpose — they are taller than the frame, so their crowns leave the picture and their trunks frame it.
const FULL_CELLS: Pt[] = (() => { const c: Pt[] = []; for (let x = -3; x <= 2; x++) for (let y = -3; y <= 2; y++) c.push([x, y]); return c; })();
const FC = -3.5;   // the 6×6 plot's centre (garden units, both axes) — where photo mode frames
const F = (b: number, a: number): Pt => [FC + (a + b) / 2, FC + (a - b) / 2];
/** garden-axis offset from the plot centre (for the layouts that are symmetrical in garden space) */
const G = (dx: number, dy: number): Pt => [FC + dx, FC + dy];
type D = [DecorId, number, number, boolean?];
const fd = (id: DecorId, p: Pt, flip?: boolean): D => [id, p[0], p[1], flip];

function fullLayout(v: number): GardenLayout {
  switch (v) {
    // 11 — THE HOME PAGE HERO: a cottage garden round a still pond. NO SPOILERS: every plant is an early unlock
    //      (level 26 and under — no roses, tulips or sunflowers, nothing fantasy), so the site's first picture
    //      shows the game a new player starts in. COMPOSED FOR THE HERO: the page's logo, line and Play button sit
    //      over the middle of the picture, so the middle is the calm pond; the detail frames it — a tree line along
    //      the top, reeds on the banks, a pebble path from the front edge to a bench beside the water with its
    //      lanterns, bird bath and borders, and a brook running out of the pond to the front-left.
    case 11: {
      const meadow = ["cosmos", "blackEyedSusan", "daisy", "marigold", "fountainGrass", "pansy"];
      return {
        cells: FULL_CELLS,
        ponds: [{ c: F(-1, 0), rx: 4.4, ry: 3.4 }],
        streams: [{ pts: [F(-5, 4), F(-9, 9), F(-12, 14), F(-15, 20)], r: 0.75 }],
        paths: [{ type: "pebble", pts: [F(13, 20), F(11, 14), F(9, 9), F(7, 5)], r: 0.6 }],
        decor: [
          // the tree line, low enough that the crowns FRAME the top of the picture instead of leaving cut trunks
          fd("oakTree", F(-12, -11)), fd("cherryTree", F(3, -12)), fd("birchTree", F(13, -11)),
          fd("pineTree", F(-17, -3)), fd("birchTree", F(17, -1)),
          fd("gardenBench", F(8, 4)), fd("lantern", F(10, 9)), fd("lantern", F(-6, -6)), fd("lantern", F(12, 16)),
          fd("birdBath", F(-10, 1)), fd("birdhouse", F(-8, -9)), fd("wateringCan", F(14, 10)),
          fd("mossyRock", F(-8, -3)), fd("pebbles", F(6, 8)), fd("rockCairn", F(-15, 11)), fd("stump", F(15, 3)),
        ],
        hosts: [["planterBox", ...F(12, 3), ["nasturtium"]]],
        beds: [
          // reeds on the BANKS (not in the water)
          { c: F(-8, -1), r: 0.8, species: ["papyrus", "horsetail", "sedge"] },
          { c: F(-3, -6), r: 0.8, species: ["sedge", "horsetail", "papyrus"] },
          { c: F(4, -3), r: 0.8, species: ["sedge", "horsetail"] },
          // the borders along the path and round the bench
          { c: F(11, 6), r: 1.1, species: ["daisy", "pansy", "marigold"] },
          { c: F(5, 10), r: 1.3, species: ["zinnia", "cosmos", "blackEyedSusan"] },
          { c: F(14, 13), r: 1.1, species: ["petunia", "crocus", "daisy"] },
          { c: F(8, 16), r: 1.2, species: ["lavenderShrub", "daisy", "pansy"] },
          // the meadow down the brook, and the front-left bed
          { c: F(-8, 9), r: 1.8, species: meadow, step: 0.8, fill: 0.8 },
          { c: F(-4, 15), r: 1.3, species: ["pansy", "crocus", "daisy"] },
          // round the bird bath
          { c: F(-12, 4), r: 1.1, species: ["lavenderShrub", "pansy", "crocus"] },
          // under the trees: ferns and shrubs
          { c: F(-13, -6), r: 1.4, species: ["bostonFern", "maidenhairFern", "buttonFern"] },
          { c: F(8, -7), r: 1.3, species: ["azalea", "miniRoseBush", "boxwood"] },
          { c: F(15, -5), r: 1.1, species: ["holly", "lavenderShrub", "boxwood"] },
          { c: F(-2, -10), r: 1.0, species: ["azalea", "boxwood"] },
        ],
      };
    }
    // 6 — WILDFLOWER BROOK: a brook from the top-right corner to the bottom-left; a loose meadow above it, a
    //     cottage lawn with a pebble walk, bench and lantern below it.
    case 6: {
      const meadow = ["poppy", "cosmos", "daisy", "blackEyedSusan", "bluebell", "foxglove", "allium", "aster", "fountainGrass", "blueFescue", "marigold"];
      const m = (b: number, a: number, r: number) => ({ c: F(b, a), r, species: meadow, step: 0.8, fill: 0.8 });
      return {
        cells: FULL_CELLS,
        streams: [{ pts: [F(19, -22), F(9, -10), F(3, -3), F(-3, 5), F(-9, 11), F(-20, 22)], r: 0.9 }],
        paths: [{ type: "pebble", pts: [F(19, 5), F(11, 7), F(5, 11), F(2, 21)], r: 0.6 }],
        decor: [
          fd("birchTree", F(-11, -15)), fd("cherryTree", F(-1, -17)), fd("birchTree", F(7, -16)), fd("birchTree", F(-17, -8)),
          fd("gardenBench", F(8, 5)), fd("lantern", F(11, 10)), fd("birdBath", F(12, -1)), fd("wateringCan", F(13, 15)),
          fd("sundial", F(8, 15)), fd("birdhouse", F(-7, -6)), fd("windChime", F(-12, 2)), fd("pinwheel", F(-4, -10)),
          fd("rockCairn", F(-1, 8)), fd("mossyRock", F(6, -3)), fd("stump", F(-14, 12)),
        ],
        hosts: [["planterBox", ...F(14, 4), ["nasturtium"]], ["windowBox", ...F(10, 13), ["ivyGeranium", "trailingLobelia", "sweetPea"]]],
        beds: [
          m(-10, -9, 2.4), m(-5, -2, 1.6), m(-8, 6, 2.0), m(-3, -13, 1.8), m(2, -8, 1.2), m(-14, -4, 1.5), m(-15, 8, 1.2),
          { c: F(14, -6), r: 1.3, species: ["rose", "peony", "lavenderShrub"] }, { c: F(3, 2), r: 1.1, species: ["lavenderShrub", "daisy", "pansy"] },
          { c: F(6, 19), r: 1.5, species: ["hydrangeaBush", "rose", "delphinium"] }, { c: F(-4, 16), r: 1.6, species: ["foxglove", "delphinium", "peony", "rose"] },
          { c: F(15, 17), r: 1.2, species: ["daisy", "tulip", "crocus"] }, { c: F(15, 9), r: 0.9, species: ["zinnia", "cosmos"] },
          { c: F(-1, 1), r: 0.8, species: ["papyrus", "horsetail", "sedge"] }, { c: F(6, -6), r: 0.8, species: ["papyrus", "horsetail"] },
          { c: F(-7, 10), r: 0.8, species: ["sedge", "horsetail", "papyrus"] },
        ],
      };
    }
    // 7 — NIGHT POND: a big still pond with a brook out of it, ringed by lanterns and torches; a fire pit with its
    //     bench and log; glow plants in every bed.
    case 7: {
      const glow = ["nightglowMushroom", "lumenCactus", "lanternMoss", "pearlFern", "crystalbloom", "willowisp", "voidOrchid", "auroraFern", "frostfern", "lightbloom", "glacierlily", "abyssBlossom"];
      return {
        cells: FULL_CELLS,
        ponds: [{ c: F(-3, 1), rx: 4.2, ry: 3.6 }],
        streams: [{ pts: [F(1, 5), F(7, 12), F(12, 22)], r: 0.8 }],
        paths: [
          { type: "stone", pts: [F(-20, 6), F(-13, 8), F(-9, 13), F(-7, 22)], r: 0.55 },
          { type: "stone", pts: [F(18, -8), F(11, -5), F(7, -1)], r: 0.55 },
        ],
        decor: [
          fd("willowTree", F(-12, -12)), fd("pineTree", F(-3, -18)), fd("cherryTree", F(10, -15)), fd("poplarTree", F(16, -12)),
          fd("lantern", F(-11, -3)), fd("lantern", F(4, -5)), fd("lantern", F(4, 8)), fd("lantern", F(-10, 8)),
          fd("fairyLights", F(-15, 3)), fd("fairyLights", F(10, 1), true),
          fd("torch", F(13, 10)), fd("torch", F(-15, -8)), fd("torch", F(1, -11)), fd("torch", F(-2, 14)),
          fd("firePit", F(10, 9)), fd("gardenBench", F(14, 6), true), fd("log", F(8, 14)), fd("stump", F(13, 14)),
          fd("gazingBall", F(-5, 11)), fd("obelisk", F(-14, 16)), fd("mossyRock", F(3, -3)),
        ],
        hosts: [["hangingBasket", ...F(6, -9), ["glassvine"]], ["stoneUrn", ...F(-12, 6), ["cometvine"]], ["hangingBasket", ...F(-7, -8), ["stringHearts"]]],
        beds: [
          { c: F(-14, -12), r: 2.0, species: glow }, { c: F(-6, -12), r: 1.4, species: glow }, { c: F(8, -10), r: 1.6, species: glow },
          { c: F(15, 0), r: 1.6, species: glow }, { c: F(-12, 14), r: 1.5, species: glow }, { c: F(3, 16), r: 1.5, species: glow },
          { c: F(15, 17), r: 1.2, species: glow }, { c: F(4, 1), r: 0.8, species: ["waterDandelion", "willowisp"] },
          { c: F(-9, 3), r: 0.8, species: ["waterDandelion", "frostfern"] }, { c: F(-2, 8), r: 0.9, species: ["willowisp", "pearlFern"] },
          { c: F(-16, 0), r: 1.2, species: ["lavenderShrub", "hydrangeaBush", "pearlFern"] },
        ],
      };
    }
    // 8 — CHERRY & STONE: a raked-sand swath with boulders set in it, a spring pool, cherry blossom and a red maple
    //     over clipped shrubs, and a paver walk crossing the sand.
    case 8: {
      return {
        cells: FULL_CELLS,
        ponds: [{ c: F(-11, -10), rx: 2.4, ry: 2.0 }],
        streams: [{ pts: [F(-10, -8), F(-15, -2), F(-21, 4)], r: 0.7 }],
        paths: [
          { type: "sand", pts: [F(-11, -2), F(-3, -1), F(5, 4), F(13, 11), F(20, 14)], r: 2.6 },
          { type: "stone", pts: [F(4, 22), F(1, 13), F(-3, 6), F(-4, -4)], r: 0.55 },
        ],
        decor: [
          fd("cherryTree", F(-3, -16)), fd("cherryTree", F(9, -14)), fd("mapleTree", F(16, -6)), fd("pineTree", F(-15, -15)),
          fd("boulder", F(-6, 0)), fd("flatBoulder", F(3, 2)), fd("mossyRock", F(9, 7)), fd("rock", F(0, -3)),
          fd("pebbles", F(6, 6)), fd("rockCairn", F(12, 12)), fd("mossyRock", F(-9, -3)),
          fd("lantern", F(-7, 7)), fd("lantern", F(10, -3)), fd("gardenBench", F(12, 16)), fd("birdBath", F(-13, 9)),
          fd("flatBoulder", F(-12, -1)), fd("rock", F(8, 3)), fd("pebbles", F(-2, 2)), fd("mossyRock", F(15, 10)),
          fd("boulder", F(-15, 11)), fd("rockCairn", F(3, -12)), fd("mossyRock", F(-6, 15)), fd("stump", F(14, -2)),
        ],
        beds: [
          { c: F(-12, 13), r: 1.6, species: ["azalea", "camellia", "rhododendron"] }, { c: F(14, 2), r: 1.4, species: ["azalea", "boxwood", "camellia"] },
          { c: F(-5, -11), r: 1.3, species: ["maidenhairFern", "mondoGrass", "iris"] }, { c: F(-16, 2), r: 1.0, species: ["iris", "papyrus"] },
          { c: F(6, 15), r: 1.4, species: ["boxwood", "mondoGrass", "blueFescue"] }, { c: F(-4, 17), r: 1.3, species: ["hydrangeaBush", "gardenia"] },
          { c: F(15, 12), r: 1.1, species: ["lavenderShrub", "boxwood"] }, { c: F(6, -9), r: 1.4, species: ["azalea", "rhododendron", "maidenhairFern"] },
          { c: F(1, -10), r: 1.0, species: ["mondoGrass", "blueFescue"] }, { c: F(-10, 4), r: 1.0, species: ["mondoGrass", "iris"] },
          { c: F(15, -12), r: 1.0, species: ["camellia", "boxwood"] },
          { c: F(-2, -13), r: 1.5, species: ["azalea", "rhododendron", "maidenhairFern", "mondoGrass"] },
          { c: F(12, -10), r: 1.3, species: ["azalea", "camellia", "blueFescue"] },
          { c: F(-14, -5), r: 1.2, species: ["maidenhairFern", "iris", "sedge"] },
          { c: F(-2, -7), r: 1.1, species: ["mondoGrass", "birdsNestFern", "maidenhairFern"] },
          { c: F(4, 9), r: 1.1, species: ["mondoGrass", "blueFescue", "azalea"] },
          { c: F(10, 20), r: 1.5, species: ["hydrangeaBush", "azalea", "boxwood"] },
          { c: F(-11, 19), r: 1.4, species: ["camellia", "gardenia", "mondoGrass"] },
          { c: F(-8, 11), r: 1.0, species: ["iris", "sedge", "mondoGrass"] },
          { c: F(15, 4), r: 0.9, species: ["blueFescue", "lavenderShrub"] },
        ],
      };
    }
    // 9 — GRAND ROSE PARTERRE: symmetrical in GARDEN axes — a fountain in a paver ring, paver cross, four rose
    //     quadrants around stone urns, topiary at the crossings, poplars behind and clipped hedges in front.
    case 9: {
      const ring: Pt[] = [];
      for (let k = 0; k <= 24; k++) { const t = (k / 24) * Math.PI * 2; ring.push(G(3.6 * Math.cos(t), 3.6 * Math.sin(t))); }
      const sq: Pt[] = [G(-7.5, -7.5), G(7.5, -7.5), G(7.5, 7.5), G(-7.5, 7.5), G(-7.5, -7.5)];
      const decor: D[] = [fd("fountain", G(0, 0))];
      for (const [x, y] of [[1, 1], [1, -1], [-1, 1], [-1, -1]] as Pt[]) {
        decor.push(fd("topiarySpiral", G(2.3 * x, 2.3 * y)));
        decor.push(fd("topiaryBall", G(1.3 * x, 5.6 * y)), fd("topiaryBall", G(5.6 * x, 1.3 * y)));
      }
      for (const t of [-5, -2.5, 0, 2.5, 5]) decor.push(fd("poplarTree", G(-9.6, t)), fd("poplarTree", G(t, -9.6)));
      decor.push(fd("gardenBench", G(8.8, -2.6)), fd("gardenBench", G(-2.6, 8.8), true), fd("lantern", G(8.6, 1.4)), fd("lantern", G(1.4, 8.6)),
        fd("obelisk", G(-8.8, -8.8)), fd("armillary", G(9.2, 9.2)), fd("sundial", G(8.8, 5.2)));
      const quad = (x: number, y: number) => ({ c: G(5.4 * x, 5.4 * y), r: 1.9, species: ["rose", "miniRoseBush", "peony", "rose"], step: 0.7 });
      return {
        cells: FULL_CELLS,
        paths: [
          { type: "stone", pts: [G(0, -12), G(0, 12)], r: 0.7 }, { type: "stone", pts: [G(-12, 0), G(12, 0)], r: 0.7 },
          { type: "stone", pts: ring, r: 0.55 }, { type: "stone", pts: sq, r: 0.5 },
        ],
        decor,
        hosts: [
          ["stoneUrn", ...G(5.4, 5.4), ["ivyGeranium"]], ["stoneUrn", ...G(-5.4, 5.4), ["englishIvy"]],
          ["stoneUrn", ...G(5.4, -5.4), ["englishIvy"]], ["stoneUrn", ...G(-5.4, -5.4), ["ivyGeranium"]],
        ],
        beds: [
          quad(1, 1), quad(1, -1), quad(-1, 1), quad(-1, -1),
          { c: G(1.9, 1.9), r: 0.7, species: ["lavenderShrub"] }, { c: G(-1.9, 1.9), r: 0.7, species: ["lavenderShrub"] },
          { c: G(1.9, -1.9), r: 0.7, species: ["lavenderShrub"] }, { c: G(-1.9, -1.9), r: 0.7, species: ["lavenderShrub"] },
        ],
        rows: [
          { from: G(9.4, -7), to: G(9.4, 7), species: ["boxwood"], step: 0.66 }, { from: G(-7, 9.4), to: G(7, 9.4), species: ["boxwood"], step: 0.66 },
          { from: G(10.4, -6), to: G(10.4, 8), species: ["hydrangeaBush", "gardenia", "lavenderShrub"], step: 0.7 },
          { from: G(-6, 10.4), to: G(8, 10.4), species: ["hydrangeaBush", "gardenia", "lavenderShrub"], step: 0.7 },
          { from: G(-8.4, -6), to: G(-8.4, 6), species: ["peony", "rose"], step: 0.7 }, { from: G(-6, -8.4), to: G(6, -8.4), species: ["peony", "rose"], step: 0.7 },
        ],
      };
    }
    // 10 — HARVEST ROWS: long planted rows between dirt paths, a working yard (hay, barrow, well, barrels) and a
    //      pair of shade trees at the back.
    default: {
      const bands: string[][] = [
        ["sunflower"], ["marigold", "zinnia"], ["pepperPlant"], ["lavenderShrub"], ["blackEyedSusan", "gerbera"],
        ["chrysanthemum", "aster"], ["sunflower", "gladiolus"], ["pepperPlant", "winterberry"], ["cosmos", "zinnia"],
      ];
      const rows: NonNullable<GardenLayout["rows"]> = [];
      let bi = 0;
      for (let y = -10; y <= 10.01; y += 1.0) {
        if (Math.abs(y + 4.5) < 1 || Math.abs(y - 4.5) < 1) continue;   // the two long paths
        rows.push({ from: G(-12, y), to: G(-1.4, y), species: bands[bi % bands.length]!, step: 0.72 });
        rows.push({ from: G(1.4, y), to: G(12, y), species: bands[(bi + 4) % bands.length]!, step: 0.72 });
        bi++;
      }
      return {
        cells: FULL_CELLS,
        paths: [
          { type: "dirt", pts: [G(-13, -4.5), G(13, -4.5)], r: 0.75 }, { type: "dirt", pts: [G(-13, 4.5), G(13, 4.5)], r: 0.75 },
          { type: "dirt", pts: [G(0, -13), G(0, 13)], r: 0.75 },
        ],
        decor: [
          fd("mapleTree", G(-9, -6.5)), fd("oakTree", G(-6.5, -9)),
          fd("dovecote", G(7, -6)), fd("dovecote", G(8.6, -3.2), true), fd("wheelbarrow", G(0.9, -2.2)), fd("wateringCan", G(-0.9, 2.4)),
          fd("wishingWell", G(-6.8, 6.8)), fd("rainBarrel", G(1.2, 6.2)), fd("barrel", G(-1.2, -6.4)), fd("gardenArch", G(-1.0, 9.2)),
          fd("pottingBench", G(6.4, 6.8)), fd("coldFrame", G(1.0, -8.2)), fd("hammock", G(-1.1, -9.8)),
        ],
        rows,
      };
    }
  }
}

// ── greenhouse ─────────────────────────────────────────────────────────────────────────────────────────

interface HouseLayout {
  racks: number;
  /** species per rack, cycled across its 12 pots (tier-major) */
  rackSpecies: string[][];
  light: string | string[];      // grow-light colour for every shelf (or per rack)
  pots?: string | string[];      // pot skin for every pot (or per rack)
  building: string;
  floor: string;
  sprinklers?: boolean;
  robot?: boolean;
  compost?: number;
  legend?: string[];             // species shown LEGEND-ranked (sparkling)
}

function houseLayout(v: number): HouseLayout {
  switch (v) {
    case 1: return {
      racks: 3, building: "glasshouse", floor: "stone", light: "white", pots: "terracotta",
      rackSpecies: [
        ["rose", "peony", "ranunculus", "carnation"],
        ["tulip", "daffodil", "hyacinth", "iris", "anemone", "crocus"],
        ["daisy", "gerbera", "cosmos", "zinnia", "poppy", "marigold"],
      ],
    };
    case 2: return {
      racks: 4, building: "victorian", floor: "encaustic", light: ["magenta", "blue", "uv", "magenta"], pots: ["porcelain", "midnight", "porcelain", "midnight"],
      rackSpecies: [
        ["voidOrchid", "prismRose", "bloodshadowRose", "lightbloom"],
        ["nightglowMushroom", "lumenCactus", "crystalbloom", "pearlFern"],
        ["auroraFern", "cometvine", "glassvine", "willowisp"],
        ["phoenixLily", "emberbloom", "glacierlily", "honeybell"],
      ],
      robot: true, legend: ["voidOrchid", "crystalbloom"],
    };
    case 3: return {
      racks: 5, building: "copper", floor: "flagstone", light: ["blue", "red", "white", "blue", "red"], pots: ["terracotta", "mossy", "terracotta", "mossy", "terracotta"],
      rackSpecies: [
        ["echeveria", "aloe", "haworthia", "agave", "henChicks", "sempervivum"],
        ["barrelCactus", "pricklyPear", "goldenCactus", "pincushionCactus", "zebraCactus", "jade"],
        ["bostonFern", "maidenhairFern", "birdsNestFern", "staghornFern", "asparagusFern", "buttonFern"],
        ["pothos", "stringPearls", "stringHearts", "burrosTail", "philodendron", "englishIvy"],
        ["fountainGrass", "blueFescue", "mondoGrass", "snakePlant", "papyrus", "purpleMillet"],
      ],
      sprinklers: true, compost: 3,
    };
    case 4: return {
      racks: 4, building: "obsidian", floor: "concrete", light: "uv", pots: ["urn", "pedestal", "urn", "pedestal"],
      rackSpecies: [
        ["lilyOfTheDead", "abyssBlossom", "vampiricPitcher", "voidOrchid"],
        ["amethystThorn", "bloodshadowRose", "magmaSucculent", "lumenCactus"],
        ["waterDandelion", "frostfern", "cloudPuff", "lanternMoss"],
        ["prismRose", "phoenixLily", "crystalbloom", "auroraFern"],
      ],
      legend: ["lilyOfTheDead", "prismRose", "abyssBlossom"], robot: true,
    };
    default: return {
      racks: 5, building: "victorian", floor: "flagstone", light: ["white", "red", "magenta", "blue", "white"], pots: ["mossy", "terracotta", "porcelain", "urn", "midnight"],
      rackSpecies: [
        ["hydrangeaBush", "azalea", "camellia", "gardenia", "rhododendron", "miniRoseBush"],
        ["hibiscus", "geranium", "petunia", "pansy", "snapdragon", "foxglove"],
        ["stringHearts", "morningGlory", "sweetPea", "ivyGeranium", "trailingLobelia", "nasturtium"],
        ["lavenderShrub", "rosemary", "delphinium", "allium", "gladiolus", "freesia"],
        ["pepperPlant", "holly", "winterberry", "boxwood", "chrysanthemum", "aster"],
      ],
      sprinklers: true, compost: 4,
    };
  }
}

function buildHouse(H: HouseLayout): Partial<SaveData> {
  const longAgo = 1_600_000_000_000;
  const plantings: Record<string, { species: string; plantedAt: number }> = {};
  const potSkins: Record<string, string> = {};
  const shelves: string[] = [];
  const growLightColors: Record<string, string> = {}, growLightTiers: Record<string, number> = {}, lightManual: Record<string, boolean> = {};
  const pick = <T,>(v: T | T[], r: number): T => (Array.isArray(v) ? v[r % v.length]! : v);
  for (let r = 0; r < H.racks; r++) {
    const list = (H.rackSpecies[r % H.rackSpecies.length] ?? []).filter((sp) => FLOWERS[sp] || (console.warn(`[showcase] unknown species ${sp}`), false));
    for (let t = 0; t < 3; t++) {
      const shelf = `${r}-${t}`;
      shelves.push(shelf);
      growLightColors[shelf] = pick(H.light, r);
      growLightTiers[shelf] = 4;
      lightManual[shelf] = true;
      for (let c = 0; c < 4; c++) {
        const key = `${r}-${t}-${c}`;
        // step 5 per tier (not 4): a 4-species rack rotates one place per shelf instead of stacking the same
        // plant straight down every column, and a 6-species rack still runs through its whole list.
        if (list.length) plantings[key] = { species: list[(t * 5 + c + r) % list.length]!, plantedAt: longAgo };
        if (H.pots) potSkins[key] = pick(H.pots, r);
      }
    }
  }
  return {
    rackCount: H.racks, plantings, potSkins, gridPower: true,
    growLights: shelves, growLightColors, growLightTiers, lightManual,
    selectedBuildingSkin: H.building, selectedFloorSkin: H.floor,
    ...(H.sprinklers ? { sprinklers: shelves.filter((_, i) => i % 2 === 0) } : {}),
    ...(H.robot ? { robot: true } : {}),
    ...(H.compost != null ? { equipment: { compostMachine: 1 }, compostTier: H.compost } : {}),
  };
}

/** Every achievement EXCEPT the legend-rank harvest ones (those make a species sparkle), plus the legend ranks
 *  a variant asks for. Unlocking the rest keeps every skin owned and stops unlock pop-ups landing over a shot. */
function achievementsFor(legend: string[]): string[] {
  const legendIds = new Set(Object.keys(FLOWERS).map((sp) => harvestAchievementId(sp, LEGEND_THRESHOLD)));
  const keep = new Set(legend.map((sp) => harvestAchievementId(sp, LEGEND_THRESHOLD)));
  return ACHIEVEMENTS.map((a) => a.id).filter((id) => !legendIds.has(id) || keep.has(id));
}

/** The save fields for showcase variant `v` (1..10; 6..10 are the full-bleed gardens). Spread over the shot preset; overrides both scenes. */
export function showcaseSave(v: number): Partial<SaveData> {
  const n = Math.max(1, Math.min(11, Math.round(v) || 1));   // 11 = the home page hero
  const H = houseLayout(((n - 1) % 5) + 1);   // 6..10 are full-bleed GARDENS; their greenhouse reuses 1..5
  return {
    ...buildGarden(n > 5 ? fullLayout(n) : gardenLayout(n), n),
    ...buildHouse(H),
    unlockedAchievements: achievementsFor(H.legend ?? []),
  };
}
